// ============================== [Teamsync API 服務層] ==============================
// 專注於團隊協作和聊天室相關業務邏輯，使用底層的 apiClient 提供的 axios 配置和認證

import { createApiClient, AuthConfig } from './apiClient';
import { AxiosInstance } from 'axios';

// ============================== [類型定義] ==============================

// Teamsync API 配置
interface TeamsyncConfig {
  baseUrl: string;
  accessToken?: string;
}

// 聊天室 AI 對話參數
interface ChatWithAiParams {
  chatroomId: string;
  agentType: string;
  channelType: string;
  tts: boolean;
  chatType: string;
  message: string;
  blobIds: string[];
  additionalArgs: string;
}

// 聊天室 AI 對話響應
interface ChatWithAiResponse {
  [key: string]: any;
}

// ============================== [配置管理] ==============================

// 獲取 Teamsync API 配置
const getTeamsyncConfig = (): TeamsyncConfig => {
  // 嘗試從多個來源獲取配置
  let baseUrl = process.env.REACT_APP_TEAMSYNC_API_URL;
  let accessToken = process.env.REACT_APP_TEAMSYNC_ACCESS_TOKEN;

  // 如果沒有環境變數，使用默認配置
  if (!baseUrl) {
    // Teamsync API 的正確 baseUrl
    baseUrl = 'https://api.scfg.io';
  }

  return {
    baseUrl,
    accessToken
  };
};

// 創建 Teamsync API 專用的認證配置
const createTeamsyncAuthConfig = (accessToken?: string): AuthConfig => {
  const config = getTeamsyncConfig();

  // 如果提供了 accessToken，使用它；否則使用配置中的或默認值
  const finalToken = accessToken || config.accessToken || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhZG1pbiIsImNvbXBhbnlfaWQiOiI4NThjNmYxNC01NGZkLTQ3YmItYTE2OS0xZWM2ODZmNGEwMmUiLCJleHAiOjE3NTAwNjkxNzd9.vs1cmMIWP2qHKCFGkYr4PxdIgLD7iLINHHi9PlF6JdM';

  return {
    mode: 'token',
    credentials: {
      staticToken: finalToken
    },
    serverUrl: config.baseUrl
  };
};

// ============================== [配置化 API 工廠函數] ==============================

// 創建配置化的 teamsyncApi 實例
export const createTeamsyncApi = (authConfig?: AuthConfig) => {
  // 使用提供的配置或默認的 Teamsync 配置
  const finalAuthConfig = authConfig || createTeamsyncAuthConfig();

  // 創建配置化的 client 獲取函數
  const getClient = async (): Promise<AxiosInstance> => {
    const client = await createApiClient(finalAuthConfig);

    // 為 Teamsync API 添加特殊的請求攔截器
    client.interceptors.request.use((config) => {
      // 將 Authorization Bearer token 轉換為 x-api-key
      if (config.headers?.['Authorization']) {
        const token = config.headers['Authorization'].toString().replace('Bearer ', '');
        delete config.headers['Authorization'];
        config.headers['x-api-key'] = token;
      }
      return config;
    });

    return client;
  };

  return {
    // ============================== [聊天室 AI 對話] ==============================

    // 與聊天室 AI 進行對話
    chatWithAi: async ({
      chatroomId,
      agentType,
      channelType,
      tts,
      chatType,
      message,
      blobIds,
      additionalArgs
    }: ChatWithAiParams): Promise<ChatWithAiResponse> => {
      try {
        const client = await getClient();

        // 構建查詢參數
        const queryParams = new URLSearchParams({
          agent_type: agentType,
          channel_type: channelType,
          tts: tts.toString()
        });

        // 構建請求 URL
        const url = `/private/chatrooms/chat/ai/${chatroomId}?${queryParams}`;

        // 構建請求體
        const requestBody = {
          chat_type: chatType,
          message: message,
          blob_ids: blobIds,
          additional_args: additionalArgs
        };

        // 發送 POST 請求
        const response = await client.post(url, requestBody);

        if (response.status !== 200) {
          throw new Error("聊天室 AI 對話失敗");
        }

        return response.data;
      } catch (error: any) {
        console.error('聊天室 AI 對話錯誤:', error);
        throw error.response?.status === 404
          ? new Error("找不到聊天室或 AI 服務")
          : error;
      }
    },

    // ============================== [聊天室 AI 對話歷史] ==============================

    // 獲取聊天室 AI 對話歷史
    getChatroomAiMessages: async (chatroomId: string, options: {
      channelType?: string;
      chatType?: string;
      limit?: number;
      visibilityFilter?: boolean;
    } = {}) => {
      try {
        const client = await getClient();
        const {
          channelType = 'chatroom',
          chatType = 'ai',
          limit = 10,
          visibilityFilter = true
        } = options;

        const queryParams = new URLSearchParams({
          channel_type: channelType,
          chat_type: chatType,
          limit: limit.toString(),
          visibility_filter: visibilityFilter.toString()
        });

        const response = await client.get(`/private/chatrooms/chat/ai/${chatroomId}?${queryParams}`);
        return response.data;
      } catch (error) {
        console.error('獲取聊天室 AI 對話歷史錯誤:', error);
        throw error;
      }
    },
  };
};

// ============================== [測試環境配置] ==============================

// 測試環境專用 teamsyncApi 創建函數
export const createTestTeamsyncApi = (baseUrl: string, accessToken: string) => {
  const testAuthConfig: AuthConfig = {
    mode: 'token',
    credentials: {
      staticToken: accessToken
    },
    serverUrl: baseUrl
  };

  return createTeamsyncApi(testAuthConfig);
};

// ============================== [默認 API 實例] ==============================

// 向後兼容：使用默認配置
export const teamsyncApi = createTeamsyncApi();

// 測試環境實例 - 使用提供的 token
export const testTeamsyncApi = createTestTeamsyncApi(
  'https://api.scfg.io',
  'tsp_0NHHVAApToUhJdFNq4LGs3eHSnzMEsDSM4Xhnm6ZxUHMTFntDkeZVw'
);
